<script setup>
import { onMounted, ref, watch } from "vue";
import ArgonAvatar from "@/components/ArgonAvatar.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { logoListStore } from "@/_store/logoStore";

const uploadStore = logoListStore();

const imageUrl = ref(null);
const iconUrl = ref(null);
const fileInput = ref(null);
const iconInput = ref(null);
const loading = ref(false);
const error = ref(null);
const snackbar = ref(null);

const closeSnackbar = () => {
  snackbar.value = null;
};

const onFileChange = (event) => {
  const file = event.target.files?.[0];
  if (!file) {
    return;
  }

  if (file.type.startsWith("image/")) {
    const reader = new FileReader();
    reader.onload = (e) => {
      imageUrl.value = e.target?.result;
      error.value = null;
    };
    reader.readAsDataURL(file);
    return;
  }

  error.value = "Por favor, seleccione un archivo de imagen valido.";
  imageUrl.value = null;
};

const onIconChange = (event) => {
  const file = event.target.files?.[0];
  if (!file) {
    return;
  }

  if (file.type.startsWith("image/")) {
    const reader = new FileReader();
    reader.onload = (e) => {
      iconUrl.value = e.target?.result;
      error.value = null;
    };
    reader.readAsDataURL(file);
    return;
  }

  error.value = "Por favor, seleccione un archivo de imagen valido.";
  iconUrl.value = null;
};

function deleteLogo() {
  imageUrl.value = null;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

function deleteIcon() {
  iconUrl.value = null;
  if (iconInput.value) {
    iconInput.value.value = "";
  }
}

function triggerFileInput() {
  fileInput.value?.click();
}

function triggerIconInput() {
  iconInput.value?.click();
}

function isEmptyImage(image) {
  return image === null || image === "";
}

function saveLogo() {
  const contract = { logo: imageUrl.value, icon: iconUrl.value };
  loading.value = true;
  uploadStore.saveLogo(contract).catch(() => null);
}

onMounted(() => {
  uploadStore.getLogo();
});

watch(
  () => uploadStore.logo,
  (newLogo) => {
    if (newLogo) {
      imageUrl.value = newLogo.logo || null;
      iconUrl.value = newLogo.icon || null;
    }
  },
  { immediate: true }
);

watch(
  () => uploadStore.sendLogo,
  (value) => {
    if (value) {
      loading.value = false;
      uploadStore.setLocalLogo(contractFromState());
      snackbar.value = {
        title: "OK",
        message: "Ajustes guardados",
        type: "primary",
        color: "white",
      };
    } else if (value === false) {
      loading.value = false;
      snackbar.value = {
        title: "Error",
        message: "No se pudo guardar.",
        type: "danger",
        color: "white",
      };
    }
  }
);

function contractFromState() {
  return {
    logo: imageUrl.value,
    icon: iconUrl.value,
  };
}
</script>

<template>
  <div class="card h-100">
    <div class="card-header pb-0"><p class="mb-0">Personalización</p></div>
    <div class="card-body">
      <div class="d-flex justify-content-center gap-3 flex-wrap">
        <div>
          <input
            id="logoInput"
            ref="fileInput"
            class="d-none"
            type="file"
            accept="image/png, image/jpeg"
            @change="onFileChange"
          />
          <div class="input-label box-empty" @click="triggerFileInput">
            <div v-if="isEmptyImage(imageUrl)">
              <span>Seleccione su logo</span>
            </div>
            <div v-else class="position-relative image-logo-load">
              <img
                v-tooltip="'Logo'"
                :src="imageUrl"
                alt="Vista previa"
              />
              <button
                type="button"
                class="btn btn-danger position-absolute top-0 end-0 clear-image"
                @click.stop="deleteLogo"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
        <div>
          <input
            id="iconFile"
            ref="iconInput"
            class="form-control d-none"
            type="file"
            accept="image/png, image/jpeg"
            @change="onIconChange"
          />
          <div class="input-label box-empty" @click="triggerIconInput">
            <div v-if="isEmptyImage(iconUrl)">
              <span class="text-xs">Seleccione su icono</span>
            </div>
            <div v-else class="position-relative image-icon-load">
              <argon-avatar
                v-tooltip="'Icono'"
                :image="iconUrl"
                size="xs"
                shadow="xs"
                alt="Vista previa"
                :circular="true"
                style="padding-top: 3px; padding-bottom: 3px"
              />
              <button
                type="button"
                class="btn btn-danger position-absolute top-0 end-0 clear-image"
                @click.stop="deleteIcon"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="error" class="alert alert-danger mt-2">{{ error }}</div>
    </div>
    <div class="card-footer">
      <div class="float-end">
        <argon-loading-button
          class="btn btn-xs bg-gradient-primary m-0"
          color="primary"
          variant="gradient"
          :label="loading ? 'Guardando...' : 'Guardar'"
          :loading="loading"
          @click="saveLogo"
        />
      </div>
    </div>
  </div>
  <div class="position-fixed top-1 end-1 z-index-2">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>
.box-empty {
  width: 150px;
  height: 130px;
  border: 1px dashed #e2e8f0;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 8px;
  font-size: 0.78rem;
  transition: border-color 0.15s ease-in-out;
}

.box-empty:hover {
  border-color: var(--bs-primary);
}

.image-logo-load {
  max-width: 70px;
  max-height: 34px;
  cursor: pointer;
}

.image-logo-load,
img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-icon-load {
  max-width: 43px;
  max-height: 43px;
  cursor: pointer;
}

.input-label {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.clear-image {
  display: none;
  width: 20px;
  height: 20px;
  padding: 0;
  font-size: 18px;
  line-height: 1;
  background: rgba(255, 0, 0, 0.7);
  color: white;
  border: none;
}

.image-logo-load:hover .clear-image {
  display: block;
}

.image-icon-load:hover .clear-image {
  display: block;
}
</style>
